import React, { useContext } from 'react';

import { Loading } from '../../components';
import { Icon, StatePanel } from '../../components/ui';
import { UserContext, useEntitlement } from '../../contexts';
import { MCPAccessSection, StripeFeeSection } from './MasterAdminSettings';
import { FeatureMatrixSection } from './EntitlementsSections';
import { StoresConsoleSection } from './stores/StoresConsoleSection';
import { GhostActivitySection } from './ghost/GhostActivitySection';
import { MASection } from './MASection';
import { CatalogApprovalsPanel } from '../molecules/CatalogApprovalsPanel';
import { SharedCatalogAdminPanel } from '../molecules/SharedCatalogAdminPanel';
import { WaRulesPanel } from '../molecules/WaRulesPanel';
import { EmailCatalogPanel } from '../molecules/EmailCatalogPanel';

const IStamp = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M8 2.5a2 2 0 012 2c0 1.2-.7 2-1 3h-2c-.3-1-1-1.8-1-3a2 2 0 012-2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    <path d="M4 10.5a1 1 0 011-1h6a1 1 0 011 1V12H4v-1.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    <path d="M3 14h10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

// Store-submitted catalogue entries awaiting RC review + the auto-approve
// toggle. Backend: .claude/features/store-catalog.md in retail-circle.com.
/** The sending rules every store follows; each store's Daily limit card shows them live. */
function WaBroadcastRulesSection() {
  return (
    <MASection anchor="whatsapp-broadcast" title="WhatsApp Broadcast" icon={<Icon name="MessageCircle" size="sm" />}>
      <WaRulesPanel />
    </MASection>
  );
}

/** Every email the platform sends, who gets it, what triggers it, how many went out. */
function EmailsSection() {
  return (
    <MASection anchor="emails" title="Emails" icon={<Icon name="Mail" size="sm" />}>
      <EmailCatalogPanel />
    </MASection>
  );
}

function CatalogApprovalsSection() {
  return (
    <MASection title="Product Database Approvals" icon={<IStamp />}>
      <CatalogApprovalsPanel />
    </MASection>
  );
}

const IBox = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M8 1.8L14 4.8V11.2L8 14.2L2 11.2V4.8L8 1.8Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    <path d="M2 4.8L8 7.8L14 4.8M8 7.8V14.2" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
  </svg>
);

// Master adders for the SHARED catalogue (brand / model / product). The
// Create Product button moved here from Store Settings — see DaoSettings.jsx.
function SharedCatalogSection() {
  return (
    <MASection title="Shared Product Database" icon={<IBox />}>
      <SharedCatalogAdminPanel />
    </MASection>
  );
}

// Platform-governance controls (MCP Access, Store Admin) split out of the
// per-store "Primary Admin" page — these govern the PLATFORM, not one store.
export function MasterAdminGovernance() {
  const { user } = useContext(UserContext);
  // The "WhatsApp Broadcast" Feature Matrix row, for the store this admin is in.
  const hasWaBroadcast = useEntitlement('wa_shared_number');

  if (!user) return <Loading />;
  if (!user.is_master_admin) {
    return (
      <div className="master-admin-denied">
        <StatePanel state="permission" headingLevel={2} />
      </div>
    );
  }

  return (
    <div className="master-admin">
      <h2>Retail Circle Admin</h2>
      <p className="master-admin__intro">
        Platform governance. Visible to Retail Circle staff only.
      </p>

      <div className="ma-sections">
        {/* Stores comes first: creating a store and seating its people is
            where a new merchant starts, and the sections below are about a
            store that already exists. */}
        <StoresConsoleSection />
        <SharedCatalogSection />
        <CatalogApprovalsSection />
        {/* Plans & Add-ons ABSORBED into Stores (Alex, 2026-10-27). It was a
            second store list, with a second search, asking the same question
            and answering it differently — a store could read "CORE" there and
            "no plan" on the console. One list, one panel, one answer. */}
        <FeatureMatrixSection />
        <MCPAccessSection />
        {/* Store Admin (the per-store team panel) MOVED to Settings → Owner
            Admin (Alex, 2026-09-01) — the backend already lets a store's own
            owner manage its team, so masters manage any store from there. */}
        <GhostActivitySection />
        {/* Merchant Chat Groups ABSORBED into Stores (Alex, 2026-10-27). A
            group belongs to a store, so it is made and managed in that
            store's panel; "every group" is the store list filtered to
            "No chat group". Product Updates, the one channel with no store,
            is pinned above that list. */}
        {/* Beta Error Reporting MOVED to Uptime → Beta reports (Alex, 2026-10-06). */}
        {/* Store Access REPLACED by Plans & Add-ons (Alex, 2026-09-30). It was
            a second, blunter answer to the same question — whole-account in or
            out, on its own flag, beside a per-feature system that already knew
            what an account had paid for. Suspension is now the "Account access"
            row on the store's card, enforced by the same switch as the rest of
            entitlements. The StoreBillingSection component and its endpoints
            still exist and still work; nothing is deleted, it is just no longer
            a separate screen. */}
        <StripeFeeSection />
        <EmailsSection />
        {hasWaBroadcast && <WaBroadcastRulesSection />}
      </div>
    </div>
  );
}
